import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, Users, Zap, ShieldAlert, ArrowUpRight, ArrowDownRight,
  Activity, Server, CreditCard, Clock, Database
} from 'lucide-react';
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { 
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area 
} from 'recharts';

const StatCard = ({ label, value, icon: Icon, color, loading, subtext }) => (
  <div className="bg-sonas-surface border border-sonas-border p-6 rounded-2xl space-y-4 shadow-sm">
    <div className="flex items-center justify-between">
      <div className={cn("h-10 w-10 rounded-xl flex items-center justify-center bg-opacity-10", color)}>
        <Icon size={20} className={color.replace('bg-', 'text-')} />
      </div>
    </div>
    <div>
      <p className="text-xs text-sonas-muted font-bold uppercase tracking-wider">{label}</p>
      {loading ? (
        <div className="h-8 w-24 bg-white/5 animate-pulse rounded mt-1" />
      ) : (
        <h3 className="text-2xl font-syne font-bold">{value}</h3>
      )}
      {subtext && <p className="text-[10px] text-sonas-dim mt-1">{subtext}</p>}
    </div>
  </div>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    userCount: 0,
    messageCount: 0,
    burnValue: 0,
    activeProjects: 0,
    chartData: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRealData = async () => {
      setLoading(true);
      try {
        // Fetch global counts
        const { count: users } = await supabase.from('profiles').select('*', { count: 'exact', head: true });
        const { count: projects } = await supabase.from('projects').select('*', { count: 'exact', head: true });
        
        // Fetch message activity for the last 7 days
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        
        const { data: messages, count: totalMsgs } = await supabase
          .from('messages')
          .select('created_at, role', { count: 'exact' })
          .gt('created_at', sevenDaysAgo.toISOString());

        // Process chart data (Daily prompt aggregation)
        const dailyCounts = {};
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        
        // Initialize days
        for(let i = 0; i < 7; i++) {
          const d = new Date();
          d.setDate(d.getDate() - i);
          dailyCounts[days[d.getDay()]] = 0;
        }

        messages?.forEach(m => {
          if (m.role === 'user') {
            const day = days[new Date(m.created_at).getDay()];
            if (dailyCounts[day] !== undefined) dailyCounts[day]++;
          }
        });

        const chartData = Object.entries(dailyCounts)
          .map(([name, gens]) => ({ name, gens }))
          .sort((a, b) => {
             const dayOrder = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
             return dayOrder.indexOf(a.name) - dayOrder.indexOf(b.name);
          });

        // Estimated burn based on user prompts (Average cost per interaction)
        const userPrompts = messages?.filter(m => m.role === 'user').length || 0;
        const burnValue = userPrompts * 0.06;

        setStats({
          userCount: users || 0,
          activeProjects: projects || 0,
          messageCount: totalMsgs || 0,
          burnValue: burnValue,
          chartData
        });
      } catch (err) {
        console.error("Dashboard Sync Error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchRealData();
  }, []);

  return (
    <div className="space-y-8 max-w-7xl mx-auto">
      <section>
        <h1 className="text-3xl font-syne font-bold mb-2">Workspace Intelligence</h1>
        <p className="text-sonas-muted text-sm">System status and usage metrics based on live database records.</p>
      </section>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard 
          label="Total Architects" 
          value={stats.userCount.toLocaleString()} 
          icon={Users} 
          color="bg-blue-500" 
          loading={loading}
          subtext="Registered users"
        />
        <StatCard 
          label="Project Nodes" 
          value={stats.activeProjects.toLocaleString()} 
          icon={Database} 
          color="bg-purple-500" 
          loading={loading}
          subtext="Total workspaces"
        />
        <StatCard 
          label="Prompt Volume (7d)" 
          value={stats.messageCount.toLocaleString()} 
          icon={Zap} 
          color="bg-sonas-accent" 
          loading={loading}
          subtext="AI interactions"
        />
        <StatCard 
          label="Compute Burn (7d)" 
          value={`$${stats.burnValue.toFixed(2)}`} 
          icon={CreditCard} 
          color="bg-amber-500" 
          loading={loading}
          subtext="Est. usage cost"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-sonas-surface border border-sonas-border rounded-2xl p-6">
          <h3 className="font-syne font-bold mb-8">Inference Distribution</h3>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats.chartData}>
                <defs>
                  <linearGradient id="colorGens" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#16a34a" stopOpacity={0.2}/>
                    <stop offset="95%" stopColor="#16a34a" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} />
                <XAxis 
                  dataKey="name" 
                  stroke="#444" 
                  fontSize={10} 
                  tickLine={false} 
                  axisLine={false} 
                />
                <YAxis 
                  stroke="#444" 
                  fontSize={10} 
                  tickLine={false} 
                  axisLine={false} 
                />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#0a0a0a', border: '1px solid #222', borderRadius: '12px' }}
                />
                <Area 
                  type="monotone" 
                  dataKey="gens" 
                  stroke="#16a34a" 
                  fillOpacity={1} 
                  fill="url(#colorGens)" 
                  strokeWidth={3} 
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-sonas-surface border border-sonas-border rounded-2xl p-6 flex flex-col items-center justify-center text-center space-y-4">
          <div className="h-16 w-16 bg-sonas-accent/10 rounded-[24px] flex items-center justify-center border border-sonas-accent/20">
            <Activity className="h-8 w-8 text-sonas-accent" />
          </div>
          <div>
            <h3 className="font-syne font-bold text-lg">System Health</h3>
            <p className="text-xs text-sonas-muted mt-2">All edge functions and database clusters are operating at nominal capacity.</p>
          </div>
          <div className="pt-4 w-full space-y-2">
            <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest text-sonas-dim">
              <span>DB Response</span>
              <span className="text-sonas-accent">12ms</span>
            </div>
            <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
              <div className="h-full bg-sonas-accent w-[98%]" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;